/**
 * File-preview type detection — the one rule that decides how a file is
 * previewed (cdb_webos#1611, #1777): Drive 2.0, the file preview window, the
 * chat, ticket attachments and the Knowledge Extractor all ask here.
 *
 * Pure and side-effect-free (no renderer/CSS imports) so callers that only
 * need the classification don't pull in FilePreviewContent's heavy renderer
 * dependencies (react-pdf, ExcelRenderer, etc.) just for a string check.
 *
 * File-type icons (`file-icon.ts`) and Drive thumbnails keep their own
 * mappings: they only decide what a file looks like in a list.
 */
import type { FileType } from './types'

/** How a file is previewed. `unsupported` means a download instead. */
export type FileKind = 'pdf' | 'office' | 'text' | 'code' | 'image' | 'video' | 'audio' | 'unsupported'

interface FileClass {
  kind: FileKind
  /** The FilePreviewContent renderer; absent for `unsupported`, which is a
   *  download. */
  format?: FileType
}

const UNSUPPORTED: FileClass = { kind: 'unsupported' }

function entries(extensions: string[], fileClass: FileClass): [string, FileClass][] {
  return extensions.map((extension) => [extension, fileClass])
}

const BY_EXTENSION = new Map<string, FileClass>([
  ['pdf', { kind: 'pdf', format: 'pdf' }],
  // DOCX/PPTX/XLSX render via the Collabora PDF (see needsOfficePdfPreview);
  // .xls and .xlsm draw the sheet grid from their raw bytes.
  ['docx', { kind: 'office', format: 'docx' }],
  ['pptx', { kind: 'office', format: 'pptx' }],
  ...entries(['xls', 'xlsx', 'xlsm'], { kind: 'office', format: 'xlsx' }),
  ...entries(['doc', 'ppt', 'xlsb'], UNSUPPORTED),
  ...entries(['md', 'markdown'], { kind: 'text', format: 'markdown' }),
  ['json', { kind: 'text', format: 'json' }],
  ['xml', { kind: 'text', format: 'xml' }],
  ['csv', { kind: 'text', format: 'csv' }],
  ...entries(['txt', 'text', 'yaml', 'yml', 'log', 'tsv'], { kind: 'text', format: 'text' }),
  ...entries(
    ['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'css', 'scss', 'sql'],
    { kind: 'code', format: 'text' },
  ),
  // The rendered page in the sandboxed frame, the source one click away (#1773).
  ...entries(['html', 'htm'], { kind: 'code', format: 'html' }),
  // The XML drawn by bpmn-js (#1777).
  ['bpmn', { kind: 'code', format: 'bpmn' }],
  ...entries(['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'], { kind: 'image', format: 'image' }),
  // Knowledge Area videos (video ingest plan): streamed by range, never
  // fetched as text.
  ...entries(['mp4', 'm4v', 'mov', 'webm'], { kind: 'video', format: 'video' }),
  ...entries(['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac', 'wma', 'opus'], { kind: 'audio', format: 'audio' }),
  // Containers hardly any browser plays, and tiff no browser renders.
  ...entries(['mkv', 'avi', 'wmv', 'flv', 'ogv', 'tiff'], UNSUPPORTED),
  ...entries(['zip', 'rar', '7z', 'tar', 'gz', 'bz2'], UNSUPPORTED),
])

const OFFICE_MIME: Record<string, FileClass> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { kind: 'office', format: 'docx' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { kind: 'office', format: 'pptx' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { kind: 'office', format: 'xlsx' },
  'application/vnd.ms-excel': { kind: 'office', format: 'xlsx' },
}

const BROWSER_IMAGE_MIME = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/bmp', 'image/webp', 'image/svg+xml'])
const BROWSER_VIDEO_MIME = new Set(['video/mp4', 'video/webm', 'video/quicktime'])

/** Only asked when the name has no known extension. */
function byMime(mime: string): FileClass {
  const type = mime.split(';')[0].trim().toLowerCase()
  if (type === 'application/pdf') return { kind: 'pdf', format: 'pdf' }
  if (OFFICE_MIME[type]) return OFFICE_MIME[type]
  if (BROWSER_IMAGE_MIME.has(type)) return { kind: 'image', format: 'image' }
  if (BROWSER_VIDEO_MIME.has(type)) return { kind: 'video', format: 'video' }
  if (type.startsWith('audio/')) return { kind: 'audio', format: 'audio' }
  if (type === 'application/json') return { kind: 'text', format: 'json' }
  if (type === 'application/xml' || type === 'text/xml') return { kind: 'text', format: 'xml' }
  if (type === 'text/markdown') return { kind: 'text', format: 'markdown' }
  if (type === 'text/csv') return { kind: 'text', format: 'csv' }
  if (type === 'text/plain') return { kind: 'text', format: 'text' }
  if (type === 'text/html') return { kind: 'code', format: 'html' }
  if (type === 'application/bpmn+xml') return { kind: 'code', format: 'bpmn' }
  return UNSUPPORTED
}

function classify(fileName: string, mime?: string | null): FileClass {
  const known = BY_EXTENSION.get(extensionOf(fileName))
  if (known) return known
  return mime ? byMime(mime) : UNSUPPORTED
}

/** How a file is previewed — by extension, the MIME type only as a fallback. */
export function classifyFile(fileName: string, mime?: string | null): FileKind {
  return classify(fileName, mime).kind
}

/** The FilePreviewContent renderer for a file, or null for `unsupported`. */
export function previewFormat(fileName: string, mime?: string | null): FileType | null {
  return classify(fileName, mime).format ?? null
}

const BINARY_FORMATS: FileType[] = ['pdf', 'docx', 'xlsx', 'pptx', 'image', 'video', 'audio']

/** Fetched and rendered as text: every format except the binary ones. */
export function isTextFormat(format: FileType | null): format is FileType {
  return format !== null && !BINARY_FORMATS.includes(format)
}

/**
 * DOCX, PPTX and XLSX render via a Collabora-converted PDF (issue #38) instead
 * of client-side OOXML renderers — callers that fetch the file's blob
 * (Drive2App, FilePreviewApp) need to know this too, so they hit the
 * `/preview` conversion endpoint instead of the raw download. A workbook comes
 * back as one page per sheet (cdb_tqs#2162); `.xls` and `.xlsm` stay on their
 * raw bytes and the sheet grid.
 */
export function needsOfficePdfPreview(fileName: string): boolean {
  return PDF_PREVIEW_EXTENSIONS.has(extensionOf(fileName))
}

/** A workbook previewed as the server's one-page-per-sheet PDF. */
export function isSheetPdfPreview(fileName: string): boolean {
  return extensionOf(fileName) === 'xlsx'
}

const PDF_PREVIEW_EXTENSIONS = new Set(['docx', 'pptx', 'xlsx'])

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.')
  return dot < 0 ? '' : fileName.slice(dot + 1).toLowerCase()
}
